import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, API_BASE } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { toast } from "sonner";
import { Receipt, WhatsappLogo, LinkSimple } from "@phosphor-icons/react";
import { useSiteInfo } from "@/lib/site";

const downloadPdf = (id, ref) => {
  const token = localStorage.getItem("avb_token");
  fetch(`${API_BASE}/bookings/${id}/invoice/pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = url; a.download = `receipt_${ref || id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    });
};

const whatsappReceipt = (id) => api.get(`/bookings/${id}/receipt-link`).then((r) => {
  if (r.data.link) window.open(`https://wa.me/?text=${encodeURIComponent(`My Avinya Bookings receipt for ${r.data.booking_ref}: ${r.data.link}`)}`, "_blank");
  else toast.error("Receipt link not available yet.");
}).catch(() => toast.error("Couldn't fetch receipt link"));

const COLORS = { paid: "bg-green-100 text-green-700", unpaid: "bg-stone-100 text-stone-600", failed: "bg-red-100 text-red-700", refunded: "bg-blue-100 text-blue-700", refund_pending: "bg-amber-100 text-amber-700" };

export default function Payments() {
  const [data, setData] = useState(null);
  const info = useSiteInfo();
  const navigate = useNavigate();
  useEffect(() => { api.get("/customer/payments").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="flex justify-center py-24"><Spinner /></div>;
  return (
    <div className="px-4 pt-5" data-testid="customer-payments">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold text-stone-900"><Receipt size={24} className="text-primary" /> Payment History</h1>
      <p className="mt-1 text-sm text-stone-500">Your bookings, amounts and receipts.</p>
      {info.razorpay_link && (
        <a data-testid="payments-razorpay-link" href={info.razorpay_link} target="_blank" rel="noreferrer" className="mt-4 flex items-center gap-3 rounded-2xl border border-primary/30 bg-accent/40 p-4 hover:bg-accent">
          <LinkSimple size={22} className="text-primary" />
          <div><p className="font-heading font-semibold text-stone-900">Pay via Razorpay link</p><p className="text-xs text-stone-500">UPI / Cards — use for an advance or balance. Mention your booking ref.</p></div>
        </a>
      )}
      <div className="mt-4 space-y-3">
        {data.payments.length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-6 text-center text-sm text-stone-400">No payments yet.</p> :
          data.payments.map((p) => (
            <div key={p.id} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`payment-${p.id}`}>
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-stone-800">{p.property_name}</p><p className="text-xs text-stone-400">{p.booking_ref} · {p.date}</p></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COLORS[p.payment_status] || "bg-stone-100"}`}>{p.payment_status}</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-stone-600">
                <span>Total {formatINR(p.total_amount)}</span><span>Advance {formatINR(p.advance_amount)}</span><span>Balance {formatINR(p.balance_amount)}{p.balance_paid ? " (paid)" : ""}</span>
              </div>
              {p.payment_status === "paid" && <button data-testid={`payment-receipt-${p.id}`} onClick={() => navigate(`/invoice/${p.id}`)} className="mt-3 rounded-full border border-primary px-3 py-1.5 text-xs font-medium text-primary">View receipt</button>}
              {p.payment_status === "paid" && <button data-testid={`payment-pdf-${p.id}`} onClick={() => downloadPdf(p.id, p.booking_ref)} className="ml-2 mt-3 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-600">Download PDF</button>}
              {p.payment_status === "paid" && <button data-testid={`payment-whatsapp-${p.id}`} onClick={() => whatsappReceipt(p.id)} className="ml-2 mt-3 inline-flex items-center gap-1 rounded-full border border-green-300 px-3 py-1.5 text-xs font-medium text-green-700"><WhatsappLogo size={14} /> WhatsApp my receipt</button>}
            </div>
          ))}
      </div>
    </div>
  );
}
